import {
  AkpError,
  DEFAULT_TONE,
  ErrorCodes,
  formatArtists,
  type SongDTO,
  TONE_CHOICES,
  type ToneName,
} from '@akpl/shared';
import {
  BackPriority,
  Button,
  Chip,
  colorFor,
  CoverImage,
  doesFocusableExist,
  FocusContext,
  getCurrentFocusKey,
  IconNote,
  setFocus,
  useBackHandler,
  useFocusable,
} from '@akpl/ui-tv';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { analytics } from '../analytics';
import { api } from '../api';
import { useApiErrorToast } from '../apiError';
import { useRouter } from '../router';
import { useStore } from '../store';
import { useT } from '../useT';
import styles from './songActions.module.css';

/**
 * How long the dialog waits for `play-now` / `queue` before it gives up and says so. 25 s, not
 * 10: the API waits on Firebase RTDB, which answered in 0.3-14 s on 2026-10-26, and an add that
 * was going to succeed showed "timeout" instead. The dialog shows its busy state meanwhile.
 */
export const SONG_ACTION_TIMEOUT_MS = 25_000;

/** The two ways out of the dialog (docs/04 §7.5). */
export type SongAction = 'play' | 'queue';

export const DIALOG_FOCUS_KEY = 'song-dialog';
export const PLAY_FOCUS_KEY = 'song-action-play';
export const QUEUE_FOCUS_KEY = 'song-action-queue';
export const toneFocusKey = (tone: ToneName) => `song-tone-${tone}`;
/** UX-306: the ids the song dialog is named and described by. */
export const SONG_DIALOG_TITLE_ID = 'song-dialog-title';
export const SONG_DIALOG_DESC_ID = 'song-dialog-desc';

export function defaultToneOf(song: SongDTO): ToneName {
  return song.tones.find((tone) => tone.isDefault)?.name ?? DEFAULT_TONE;
}

export interface SongActionsApi {
  /**
   * Enter on any song — a Home card, a Playlist row, a Search result — opens the song action
   * dialog (docs/04 §7.5). It replaces the old 2-option popover everywhere.
   */
  openSongActions(song: SongDTO): void;
  /** Render this next to the screen content; it is a focus boundary with its own Back handler. */
  overlay: ReactNode;
}

/**
 * Shared "what do I do with this song" flow for Home, Playlist and Search: one dialog with the
 * tone chips and the two actions → `POST /sessions/:sid/play-now` or `POST /sessions/:sid/queue`.
 */
export interface SongActionsOptions {
  /**
   * `play-now` answered: called instead of navigating to the player. The queue drawer opens the
   * dialog from inside the Player, which switches its current item rather than pushing a route.
   */
  onPlayNow?: (played: { itemId: string; songId: string; tone: ToneName }) => void;
}

/** The queued or playing entry for a song, if the room already has it. */
function findQueuedEntry(songId: string): { id: string; status: string } | null {
  const { items, order } = useStore.getState().queue;
  for (const id of order) {
    const item = items[id];
    if (item && item.songId === songId && (item.status === 'queued' || item.status === 'playing')) {
      return { id, status: item.status };
    }
  }
  return null;
}

export function useSongActions({ onPlayNow }: SongActionsOptions = {}): SongActionsApi {
  const { t } = useT();
  const [song, setSong] = useState<SongDTO | null>(null);
  const [tone, setTone] = useState<ToneName>(DEFAULT_TONE);
  // Which action is in flight (drives the spinner), null when idle.
  const [busy, setBusy] = useState<SongAction | null>(null);
  const inFlight = useRef<AbortController | null>(null);
  const navigate = useRouter((s) => s.navigate);
  const showToast = useStore((s) => s.showToast);
  const toastError = useApiErrorToast();

  const close = useCallback(() => {
    // Back while a request is out: drop it, nothing is waiting for the answer any more.
    inFlight.current?.abort();
    inFlight.current = null;
    setSong(null);
    setBusy(null);
  }, []);

  const run = useCallback(
    async (target: SongDTO, action: SongAction, chosen: ToneName) => {
      const sid = useStore.getState().session?.id;
      if (!sid || busy) return;
      setBusy(action);
      const ctrl = new AbortController();
      inFlight.current = ctrl;
      // Both calls get the same 10 s budget (no retries on POST): a silent API used to leave
      // the dialog looking frozen for up to 15 s with no sign anything was happening.
      const budget = { signal: ctrl.signal, timeoutMs: SONG_ACTION_TIMEOUT_MS };
      try {
        const body = { songId: target.id, tone: chosen, via: 'tv' as const };
        if (action === 'play') {
          let itemId: string;
          try {
            itemId = (await api.playNow({ params: { sid }, body, ...budget })).item.id;
          } catch (err) {
            // The song is already in the queue: "play now" means play THAT entry, not refuse
            // with "already in your queue" and leave the room where it was.
            const queued = AkpError.is(err) && err.code === ErrorCodes.duplicate_in_queue;
            const hinted = queued
              ? (err.details as { itemId?: unknown } | undefined)?.itemId
              : null;
            const existing =
              typeof hinted === 'string'
                ? { id: hinted, status: findQueuedEntry(target.id)?.status ?? 'queued' }
                : queued
                  ? findQueuedEntry(target.id)
                  : null;
            if (!existing) throw err;
            if (existing.status !== 'playing') {
              await api.playNowItem({ params: { sid, itemId: existing.id }, ...budget });
            }
            itemId = existing.id;
          }
          analytics.track('queue_add', { via: 'tv', playNow: true });
          close();
          if (onPlayNow) onPlayNow({ itemId, songId: target.id, tone: chosen });
          else navigate('player', { itemId, songId: target.id, tone: chosen });
        } else {
          await api.enqueue({ params: { sid }, body, ...budget });
          analytics.track('queue_add', { via: 'tv', playNow: false });
          close();
          showToast(t('common:queue.added'), 'success');
        }
      } catch (err) {
        // A 403 (`feature_required` / `subscription_required`) lands here and is toasted by the
        // shared helper — phase 1 never predicts it client-side (docs/07 §1).
        if (ctrl.signal.aborted && inFlight.current !== ctrl) return; // the user backed out
        const code = toastError(err);
        // A timeout / dropped connection leaves the dialog up so the same press can be
        // retried; every other refusal (duplicate, limit, 403…) closes it as before.
        if (code === 'timeout' || code === 'network_error') {
          inFlight.current = null;
          setBusy(null);
        } else {
          close();
        }
      }
    },
    [busy, close, navigate, onPlayNow, showToast, t, toastError],
  );

  // Phase 1: access is all-or-nothing, so no song is ever locked (docs/07 §1) and the dialog
  // opens for every song. A 403 from the API is still handled, in `run`.
  const openSongActions = useCallback((target: SongDTO) => {
    setTone(defaultToneOf(target));
    setSong(target);
  }, []);

  // Every −2..+2 chip is available to a session with access (docs/07 §1).
  const selectTone = useCallback((next: ToneName) => setTone(next), []);

  const overlay = song ? (
    <SongActionDialog
      song={song}
      tone={tone}
      onTone={selectTone}
      onClose={close}
      busy={busy}
      onConfirm={(action) => void run(song, action, tone)}
    />
  ) : null;

  return { openSongActions, overlay };
}

interface SongActionDialogProps {
  song: SongDTO;
  tone: ToneName;
  onTone: (tone: ToneName) => void;
  onClose: () => void;
  /** The action in flight, or null. */
  busy: SongAction | null;
  onConfirm: (action: SongAction) => void;
}

/**
 * Figma TV-03b 41:544 — 880×711 panel at (520, 185): cover + title/artists, the five tone
 * chips (0 selected by default, cyan outline), "Reproducir ahora" (focused on open) and
 * "Agregar a la fila". Back closes it and does nothing.
 */
function SongActionDialog({ song, tone, onTone, onClose, busy, onConfirm }: SongActionDialogProps) {
  const { t, i18n } = useT();
  const locale = i18n.language === 'en' ? 'en' : 'es';
  const previous = useRef<string | null>(null);
  const { ref, focusKey } = useFocusable<HTMLDivElement>({
    focusKey: DIALOG_FOCUS_KEY,
    trackChildren: true,
    isFocusBoundary: true,
    preferredChildFocusKey: PLAY_FOCUS_KEY,
  });

  useBackHandler(
    () => {
      onClose();
      return true;
    },
    { priority: BackPriority.modal },
  );

  // Remember the opener, focus "Reproducir ahora", restore the row/card on close.
  useEffect(() => {
    previous.current = getCurrentFocusKey() ?? null;
    void setFocus(PLAY_FOCUS_KEY);
    return () => {
      const prev = previous.current;
      if (prev && doesFocusableExist(prev)) void setFocus(prev);
    };
  }, []);

  return (
    <FocusContext.Provider value={focusKey}>
      <div className={styles.scrim} role="presentation" data-testid="song-dialog-scrim">
        <div
          ref={ref}
          role="dialog"
          aria-modal="true"
          /*
           * UX-306: `aria-label` alone left `aria-labelledby`/`aria-describedby` null, so the
           * one dialog the room opens most was the only overlay a screen reader could not
           * describe. It is named by its own title line and described by the tone legend +
           * hint, exactly like `Dialog` (whose pattern this panel predates).
           */
          aria-labelledby={SONG_DIALOG_TITLE_ID}
          aria-describedby={SONG_DIALOG_DESC_ID}
          data-focus-scope="song-dialog"
          data-focus-default={PLAY_FOCUS_KEY}
          className={styles.panel}
          data-testid="song-dialog"
        >
          <div className={styles.header}>
            <span className={styles.cover} style={{ background: colorFor(song.id) }}>
              <CoverImage
                src={song.imageUrl}
                width={112}
                height={112}
                fallback={<IconNote size={44} />}
              />
            </span>
            <span className={styles.headerText}>
              <div className={styles.title} id={SONG_DIALOG_TITLE_ID}>
                {song.title}
              </div>
              {/* A song credited only to a WP0.8 placeholder act has no artist line at all. */}
              {song.artists.length > 0 ? (
                <div className={styles.artists}>{formatArtists(song.artists, locale)}</div>
              ) : null}
            </span>
          </div>

          <h2 className={styles.legend}>{t('tv:tone.title')}</h2>
          <div className={styles.chips} role="radiogroup" aria-label={t('common:tone.label')}>
            {TONE_CHOICES.map((name) => (
              <Chip
                key={name}
                focusKey={toneFocusKey(name)}
                selected={name === tone}
                className={styles.chip}
                onSelect={() => onTone(name)}
              >
                {name}
              </Chip>
            ))}
          </div>
          <p className={styles.note} id={SONG_DIALOG_DESC_ID}>
            {t('tv:tone.hint')}
          </p>

          <div className={styles.actions}>
            <Button
              focusKey={PLAY_FOCUS_KEY}
              variant="primary"
              className={styles.action}
              loading={busy === 'play'}
              data-testid="action-play-now"
              onSelect={() => onConfirm('play')}
            >
              {t('tv:playlist.playNow')}
            </Button>
            <Button
              focusKey={QUEUE_FOCUS_KEY}
              className={styles.action}
              loading={busy === 'queue'}
              data-testid="action-add-queue"
              onSelect={() => onConfirm('queue')}
            >
              {t('tv:playlist.addToQueue')}
            </Button>
          </div>
          <p className={styles.caption}>{t('tv:songDialog.backHint')}</p>
        </div>
      </div>
    </FocusContext.Provider>
  );
}
